/**
 * apiClient - lightweight fetch wrapper that auto-attaches API key headers
 *
 * Behavior:
 * - Reads API key from `import.meta.env.VITE_QreditCharge_API_KEY` or localStorage `QreditCharge_API_KEY`.
 * - If a key is present, adds `Authorization: Bearer <key>` header (and preserves existing headers).
 * - If no key is present, forwards request unchanged (no headers added) to avoid breaking tests.
 */

export interface ApiResponse<T = any> {
  ok: boolean;
  status: number;
  data: T;
  json(): Promise<T>;
}

function createApiResponse<T>(data: T, status: number = 200): ApiResponse<T> {
  const ok = status >= 200 && status < 300;
  return {
    ok,
    status,
    data,
    json: () => Promise.resolve(data)
  };
}

export async function apiFetch(url: string, init?: RequestInit): Promise<ApiResponse> {
  try {
    const envKey = import.meta.env.VITE_QreditCharge_API_KEY as string | undefined;
    const lsKey = typeof window !== 'undefined' ? window.localStorage?.getItem('QreditCharge_API_KEY') || undefined : undefined;
    const key = envKey || lsKey;
    
    const baseHeaders = (init && init.headers) ? init.headers as Record<string, string> : {} as Record<string, string>;
    const headers: Record<string, string> = { ...baseHeaders };
    if (key && !headers['Authorization']) headers['Authorization'] = `Bearer ${key}`;
    if (key && !headers['x-api-key']) headers['x-api-key'] = key;
    
    const response = await fetch(url, { ...init, headers });
    const data = await response.json().catch(() => ({}));
    
    return createApiResponse(data, response.status);
  } catch (error) {
    if (error instanceof Error) {
      const enhancedError = new Error(`API request failed: ${error.message}`);
      (enhancedError as any).cause = error;
      throw enhancedError;
    }
    throw new Error('API request failed: Unknown error');
  }
}

export default { apiFetch };
